
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarIcon, List, Plus, Search } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";

const tasks = [
  { 
    id: 1, 
    title: "RBI Quarterly Return Filing", 
    description: "Prepare and submit quarterly return to RBI", 
    dueDate: new Date("2025-05-15"), 
    status: "pending", 
    priority: "high",
    type: "Regulatory",
    assignedTo: "Anil Kumar"
  },
  { 
    id: 2, 
    title: "Financial Covenant Verification", 
    description: "Verify and ensure compliance with financial covenants for HDFC Bank loan", 
    dueDate: new Date("2025-05-10"), 
    status: "in-progress", 
    priority: "medium",
    type: "Internal",
    assignedTo: "Priya Sharma" 
  },
  { 
    id: 3, 
    title: "Asset Liability Report", 
    description: "Prepare monthly asset liability management report", 
    dueDate: new Date("2025-05-08"), 
    status: "pending", 
    priority: "high",
    type: "Regulatory",
    assignedTo: "Vikram Singh" 
  },
  { 
    id: 4, 
    title: "Update Credit Policy", 
    description: "Review and update credit policy based on new RBI guidelines", 
    dueDate: new Date("2025-05-20"), 
    status: "pending", 
    priority: "low",
    type: "Policy",
    assignedTo: "Meera Patel" 
  },
];

const Tasks = () => {
  const [view, setView] = useState<"list" | "calendar">("list");
  const [date, setDate] = useState<Date | undefined>(new Date());
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tasks</h1>
          <p className="text-muted-foreground">
            Manage and track all your compliance and financial tasks.
          </p>
        </div>
        <Button className="bg-primary">
          <Plus className="mr-2 h-4 w-4" /> New Task
        </Button>
      </div>

      <div className="flex flex-col space-y-4">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle>Task Management</CardTitle>
              <div className="flex items-center gap-2">
                <Button 
                  variant={view === "list" ? "default" : "outline"} 
                  size="sm"
                  onClick={() => setView("list")}
                >
                  <List className="h-4 w-4" />
                </Button>
                <Button 
                  variant={view === "calendar" ? "default" : "outline"} 
                  size="sm"
                  onClick={() => setView("calendar")}
                >
                  <CalendarIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <CardDescription>
              You have {tasks.length} tasks, with 2 marked as high priority.
            </CardDescription>

            <div className="flex items-center space-x-2 pt-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search tasks..."
                  className="pl-8 w-full"
                />
              </div>
              <Select defaultValue="all-status">
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-status">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              <Select defaultValue="all-priority">
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-priority">All Priority</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[160px] justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all">
              <TabsList>
                <TabsTrigger value="all">All Tasks</TabsTrigger>
                <TabsTrigger value="regulatory">Regulatory</TabsTrigger>
                <TabsTrigger value="internal">Internal</TabsTrigger>
                <TabsTrigger value="policy">Policy</TabsTrigger>
              </TabsList>
              
              <TabsContent value="all" className="border rounded-md mt-4">
                <div className="divide-y">
                  {tasks.map((task) => (
                    <div key={task.id} className="flex items-start justify-between p-4 hover:bg-muted/50">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{task.title}</h4>
                          <div className={`px-2 py-0.5 text-xs rounded-full ${
                            task.priority === 'high' 
                              ? 'bg-red-100 text-red-800' 
                              : task.priority === 'medium'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-blue-100 text-blue-800'
                          }`}>
                            {task.priority}
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">{task.description}</p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>Type: {task.type}</span>
                          <span>Assigned to: {task.assignedTo}</span>
                          <span>Due: {format(task.dueDate, "dd MMM yyyy")}</span>
                        </div>
                      </div>
                      <div>
                        <Select defaultValue={task.status}>
                          <SelectTrigger className="w-[120px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="in-progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="regulatory">
                <div className="border rounded-md mt-4 p-4">
                  <div className="flex items-center justify-center h-32">
                    <p className="text-muted-foreground">Filter applied for regulatory tasks.</p>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="internal">
                <div className="border rounded-md mt-4 p-4">
                  <div className="flex items-center justify-center h-32">
                    <p className="text-muted-foreground">Filter applied for internal tasks.</p>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="policy">
                <div className="border rounded-md mt-4 p-4">
                  <div className="flex items-center justify-center h-32">
                    <p className="text-muted-foreground">Filter applied for policy tasks.</p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Tasks;
